import { createUnifiedTheme, palettes } from '@backstage/theme';

import './tokens.css';
import './techdocs.css';

/**
 * The revenexx portal themes (DX-275).
 *
 * Two themes, light by default, matching the public Developer Portal that
 * Michael named as the benchmark. Before this there was exactly one theme, and
 * it painted the whole page with a violet gradient: `background.default` was
 * `transparent` so a `body::before` image showed through, cards were
 * semi-transparent violet on violet, and `navigation.selectedColor` was the
 * brand yellow. Contrast was low and colour carried no information because
 * everything was coloured — that is what "sieht sehr Alpha aus" meant.
 *
 * `tokens.css` is where a colour is decided, and the TechDocs reader — which no
 * theme object can reach, because it renders MkDocs' own HTML — reads those
 * same variables.
 *
 * ONE RULE, learned twice the hard way: a `var()` belongs only where the value
 * reaches the DOM as CSS — the `components` overrides below, and
 * `techdocs.css`. Never in the PALETTE. Material-UI computes on parts of it
 * (`alpha(text.primary)` builds every Button), Backstage computes on others
 * (`darken(link)`, and `pageTheme.fontColor` when a Page mounts), and the
 * sidebar's `navigation` colours are read into JSS classes where a var()
 * resolved to nothing and left the submenu's text unreadable. None of these
 * fail loudly: they throw "Unsupported color" onto a blank page, or they
 * silently inherit the wrong colour. So the palette restates the values as
 * literals, and they have to be kept in step with `tokens.css` by hand.
 */

/** Reads a custom property so the theme and the stylesheet cannot disagree. */
const token = (name: string) => `var(--${name})`;

/**
 * The brand ramp as literal values.
 *
 * MUI derives the `light`/`dark` variants of `primary`/`secondary` from these,
 * and Backstage's default component themes call darken()/lighten() on `link`,
 * so a `var()` in those keys throws "Unsupported color" while the theme is
 * being created. Keep in step with the `--color-brand-*` block in `tokens.css`.
 */
const brand = {
  300: '#A090E0',
  400: '#8977CD',
  500: '#620DFF',
  600: '#5B18C6',
  700: '#2C1C65',
  900: '#0B0522',
} as const;

/**
 * Surface and text values, per mode, as literals.
 *
 * Material-UI v4 computes on more of the palette than the keys it documents as
 * colours: `alpha(palette.text.primary)` builds every Button's hover state,
 * `background.default`, `divider` and `action.active` are read the same way.
 * A `var()` in any of them throws "Unsupported color" the moment a component
 * mounts — which renders as a blank page, not a console warning.
 *
 * So the palette is literal and `components` below is where the tokens are
 * used. The two are the same values; `tokens.css` stays the place a colour is
 * decided, and these follow it.
 */
const surfaces = {
  light: {
    bg: '#F6F5F9',
    elevated: '#FFFFFF',
    muted: '#EDECF3',
    border: '#EDECF3',
    text: '#0B0522',
    textMuted: '#3B364C',
  },
  dark: {
    bg: '#0B0522',
    elevated: '#17122A',
    muted: '#17122A',
    border: 'rgba(255, 255, 255, 0.08)',
    text: '#F0EEF6',
    textMuted: '#8977CD',
  },
} as const;

/**
 * The sidebar, per mode.
 *
 * It is LIGHT, and that is the correction that matters: every other revenexx
 * surface — the public Developer Portal, Atlas, the Cockpit — puts a dark bar
 * across the TOP and keeps the navigation column white (`#fff`, `#0F0A26` in
 * dark mode). An earlier pass here made the sidebar the dark anchor instead,
 * which is a different design language wearing the same palette.
 *
 * Literal rather than tokens: these values are read into JSS classes for the
 * sidebar items and their submenus, where a `var()` resolves to nothing.
 */
const navigationFor = (mode: 'light' | 'dark') => ({
  background: mode === 'light' ? '#FFFFFF' : '#0F0A26',
  indicator: brand[500],
  color: mode === 'light' ? 'rgba(11, 5, 34, 0.80)' : '#C9C8D3',
  selectedColor: mode === 'light' ? brand[500] : '#FFFFFF',
  navItem: {
    hoverBackground:
      mode === 'light'
        ? 'rgba(98, 13, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.06)',
  },
  submenu: {
    background: mode === 'light' ? '#F6F5F9' : '#17122A',
  },
});

/**
 * Page headers. The old theme gave all four page types the same violet
 * gradient, which is how the colour ended up everywhere. A header is a heading,
 * not a hero, so it sits on the page background and lets the type carry it.
 *
 * Literal, not tokens: Backstage computes a contrast text colour from
 * `fontColor`, so a `var()` here throws "Unsupported color" — and because a
 * page theme is resolved when a Page mounts rather than when the theme is
 * built, the throw lands on the entity pages rather than at start-up.
 */
const flatPageTheme = (mode: 'light' | 'dark') => ({
  colors: [surfaces[mode].bg, surfaces[mode].bg],
  shape: 'none',
  backgroundImage: 'none',
  fontColor: mode === 'light' ? brand[700] : surfaces.dark.text,
});

const pageThemeFor = (mode: 'light' | 'dark') => ({
  home: flatPageTheme(mode),
  documentation: flatPageTheme(mode),
  tool: flatPageTheme(mode),
  other: flatPageTheme(mode),
});

const typography = {
  fontFamily: token('font-sans'),
  htmlFontSize: 16,
  h1: {
    fontSize: token('text-h1'),
    lineHeight: token('text-h1-line-height'),
    fontWeight: 700,
    marginBottom: 8,
  },
  h2: {
    fontSize: token('text-h2'),
    lineHeight: token('text-h2-line-height'),
    fontWeight: 700,
    marginBottom: 6,
  },
  h3: {
    fontSize: token('text-h3'),
    lineHeight: token('text-h3-line-height'),
    fontWeight: 600,
    marginBottom: 6,
  },
  h4: {
    fontSize: token('text-h4'),
    lineHeight: token('text-h4-line-height'),
    fontWeight: 600,
    marginBottom: 4,
  },
  h5: {
    fontSize: token('text-body-large'),
    lineHeight: token('text-body-large-line-height'),
    fontWeight: 600,
    marginBottom: 4,
  },
  h6: {
    fontSize: token('text-body'),
    lineHeight: token('text-body-line-height'),
    fontWeight: 600,
    marginBottom: 4,
  },
};

/**
 * Overrides that used to live as `!important` attribute-substring selectors in
 * `public/index.html` (659 lines, 172 `!important`). Expressed here they are
 * typed, reviewable, and survive a Backstage class-name change.
 */
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      // The tinted ground the frame floats on (DX-352). The page inside the
      // frame is painted by the frame, so `--ui-bg` is no longer what shows
      // behind the content.
      body: {
        backgroundColor: token('ui-ground'),
        color: token('ui-text'),
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: token('ui-bg-elevated'),
        backgroundImage: 'none',
        border: `1px solid ${token('ui-border')}`,
        borderRadius: 'var(--radius-card)',
        // The old glass recipe layered blur + a violet border on every Paper,
        // then needed a second rule to undo the doubling on nested ones. What
        // replaced it was a flat `none`, which went too far the other way: a
        // white card on a near-white ground with a border barely darker than
        // either has no edge at all, and the page read as areas printed onto
        // it. One slight step, from the token layer, so it follows the theme —
        // it resolves to `none` in dark mode, where a shadow cannot separate a
        // light surface from a darker ground anyway.
        boxShadow: 'var(--shadow-card)',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: token('ui-primary'),
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      },
    },
  },
  // MuiTypography carries NO override at all. It paints every piece of text in
  // the portal, the navigation included, and a blanket colour there overrode
  // the sidebar's own and made the submenu entries unreadable. Text inherits
  // from the surface it sits on, and `palette.text.secondary` already tones
  // the secondary variant — nothing here has to say it again.
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: `1px solid ${token('ui-border')}` },
      head: {
        color: token('ui-text-muted'),
        fontWeight: 600,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        backgroundColor: token('ui-bg-muted'),
        color: token('ui-text'),
        borderRadius: 'var(--ui-radius)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 'var(--ui-radius)',
        textTransform: 'none' as const,
        fontWeight: 600,
      },
      /*
        The palette's primary is a paler violet in dark mode, which is right
        for links and focus rings and wrong for the one button a page wants
        pressed: on the dark ground it faded into the page ("Source Code" on
        /jam). The public portals make that button yellow in dark mode instead
        (DX-352). Only the contained variant — `primary` also colours tabs,
        checkboxes and outlined buttons, and yellow is not an icon colour.
      */
      containedPrimary: {
        backgroundColor: token('ui-button-primary-bg'),
        color: token('ui-button-primary-text'),
        '&:hover': {
          backgroundColor: token('ui-button-primary-bg-hover'),
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: token('ui-bg-elevated'),
        borderRadius: 'var(--ui-radius)',
      },
    },
  },
  /*
    ── The frame (DX-352) ─────────────────────────────────────────────────
    Backstage lays its shell out against the WINDOW: the sidebar is
    `position: fixed` at its left edge, SidebarPage pads the content by the
    sidebar's width, and every Page is a scroll container `100vh` tall. The
    public portals put the navigation inside a framed page instead.

    Root.tsx makes its frame the containing block for fixed descendants
    (`contain: layout`), so the sidebar is fixed to the FRAME without an
    override of its position. What remains is below: three overrides of
    Backstage core classes, kept to height and padding. Check them on every
    Backstage upgrade — each names a class Backstage could rename.
  */
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        // The portals' `.docs-sidebar-shell` edge: a hairline, lighter than
        // the borders between cards (DX-352).
        borderRight: `1px solid ${token('ui-nav-rule')}`,
      },
    },
  },
  /*
    Backstage's own sidebar row, which only Notifications still uses — every
    other row is SidebarNavItem. Brought to that row's geometry (the portals'
    `.docs-sidebar-link`, DX-352) so the last entry in the column is not the
    one 48px bold bar left in it. Check on every Backstage upgrade: it names
    classes Backstage could rename.
  */
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        height: 32,
        width: 'calc(100% - 1rem)',
        margin: '0 0.5rem 2px',
        padding: '0 0.5rem',
        gap: '0.625rem',
        boxSizing: 'border-box' as const,
        borderRadius: '0.375rem',
        color: token('ui-nav-link'),
      },
      open: {
        '@media (min-width:600px)': {
          width: 'calc(100% - 1rem)',
        },
      },
      closed: {
        width: 'calc(100% - 1rem)',
        padding: 0,
      },
      iconContainer: {
        width: 'auto',
        height: 'auto',
        marginRight: 0,
        color: token('ui-nav-icon'),
      },
      label: {
        width: 'auto',
        fontSize: '0.875rem',
        fontWeight: 400,
      },
      selected: {
        '&$root': {
          borderLeft: 'none',
          background: token('ui-nav-active-bg'),
          color: token('ui-nav-link-active'),
        },
        '& $iconContainer': {
          marginLeft: 0,
        },
        '& $closedItemIcon': {
          paddingRight: 0,
        },
      },
    },
  },
  BackstageSidebarPage: {
    styleOverrides: {
      root: {
        // Fill the frame rather than the window. `overflowY` is a fallback
        // for a plugin page that renders no Page of its own and would
        // otherwise have nothing to scroll it.
        height: '100%',
        overflowY: 'auto' as const,
        /*
          Documentation pages collapse the sidebar to its icons (DX-352,
          point 4): Root.tsx unpins it there, and marks the frame with
          `data-shell-reading`. SidebarPage cannot see that — it pads by its
          OWN pin state — so the padding follows here. 72px is Backstage's
          `drawerWidthClosed`; 600px its `sm`, below which the sidebar is a
          bar along the bottom and pads nothing on the left.
        */
        '@media (min-width: 600px)': {
          '[data-shell-reading] &': {
            paddingLeft: 72,
          },
        },
      },
    },
  },
  BackstagePage: {
    styleOverrides: {
      // `100vh` would make every page as tall as the window inside a frame
      // that is shorter than it, and the bottom of each page would sit below
      // the frame's edge.
      root: {
        height: '100%',
      },
    },
  },
  /*
    A matched word in a search result, on the search page: by weight, as the
    palette marks it, rather than in the yellow of a browser's `<mark>` (DX-341).
    The portal keeps one accent, for what a reader can act on, and the same
    passage should not be marked two ways in the two places it appears. Check
    on every Backstage upgrade: it names a class Backstage could rename.
  */
  BackstageHighlightedSearchResultText: {
    styleOverrides: {
      highlight: {
        background: 'none',
        color: token('ui-text'),
        fontWeight: 600,
        // In a result's title the word is part of the link, and keeps its
        // colour: drawn in the text colour it read as a word that is not.
        'a &': {
          color: 'inherit',
        },
      },
    },
  },
  // The sidebar's separators were `display: none` under the old CSS, so the
  // five <SidebarDivider /> elements in Root.tsx rendered nothing at all. They
  // group Home/Create, the four fields, and the global views — which is
  // exactly the structure the navigation was missing — so they are visible
  // again, as a hairline on the dark sidebar rather than MUI's default grey.
  BackstageSidebarDivider: {
    styleOverrides: {
      root: {
        background: 'rgba(255, 255, 255, 0.08)',
      },
    },
  },
};

const shared = {
  defaultPageTheme: 'home',
  fontFamily: token('font-sans'),
  typography,
  components,
};

export const revenexxLight = createUnifiedTheme({
  ...shared,
  pageTheme: pageThemeFor('light'),
  palette: {
    ...palettes.light,
    primary: { main: brand[500] },
    secondary: { main: brand[700] },
    background: {
      default: surfaces.light.bg,
      paper: surfaces.light.elevated,
    },
    text: {
      primary: surfaces.light.text,
      secondary: surfaces.light.textMuted,
    },
    border: surfaces.light.border,
    link: brand[500],
    linkHover: brand[600],
    navigation: navigationFor('light'),
  },
});

export const revenexxDark = createUnifiedTheme({
  ...shared,
  pageTheme: pageThemeFor('dark'),
  palette: {
    ...palettes.dark,
    primary: { main: brand[400] },
    secondary: { main: brand[300] },
    background: {
      default: surfaces.dark.bg,
      paper: surfaces.dark.elevated,
    },
    text: {
      primary: surfaces.dark.text,
      secondary: surfaces.dark.textMuted,
    },
    border: surfaces.dark.border,
    link: brand[400],
    linkHover: brand[300],
    navigation: navigationFor('dark'),
  },
});
